import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePortfolios } from "@/hooks/usePortfolios";

const addProductSchema = z.object({
  name: z.string().min(1, "Product name is required"),
  description: z.string().optional(),
  manufacturer: z.string().optional(),
  portfolioId: z.string().min(1, "Portfolio is required"),
  greenSealStandardId: z.string().min(1, "Green Seal standard is required"),
});

type AddProductFormValues = z.infer<typeof addProductSchema>;

interface AddGSProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddGSProductDialog({ open, onOpenChange }: AddGSProductDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { portfolios } = usePortfolios();

  const form = useForm<AddProductFormValues>({
    resolver: zodResolver(addProductSchema),
    defaultValues: {
      name: "",
      description: "",
      manufacturer: "",
      portfolioId: "",
      greenSealStandardId: "",
    },
  });

  // Green Seal standards (hardcoded for now, could be fetched from database)
  const greenSealStandards = [
    { id: "c2448941-9ad7-47f2-afbf-1fe0b24d75b0", name: "GS-37: General Purpose Cleaners", identifier: "GS-37" },
    { id: "gs-08", name: "GS-08: Paper Towels and Napkins", identifier: "GS-08" },
    { id: "gs-09", name: "GS-09: Personal Care and Cosmetic Products", identifier: "GS-09" },
    { id: "gs-11", name: "GS-11: Paints and Coatings", identifier: "GS-11" },
    { id: "gs-40", name: "GS-40: Cleaning Services", identifier: "GS-40" },
  ];

  async function onSubmit(data: AddProductFormValues) {
    setIsSubmitting(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("You must be logged in to add products");
        return;
      }

      const { error } = await supabase
        .from('products')
        .insert({
          name: data.name,
          description: data.description,
          manufacturer: data.manufacturer,
          portfolio_id: data.portfolioId,
          product_type: 'formulated',
          certification_status: 'pending'
        });

      if (error) {
        console.error("Error adding product:", error);
        toast.error("Failed to add product. Please try again.");
        return;
      }

      toast.success("Product added successfully!");
      form.reset();
      onOpenChange(false);
      
      // Refresh the page to show the new product
      window.location.reload();
    } catch (error) {
      console.error("Exception adding product:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New Product</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Product Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter product name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Product description (optional)" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="manufacturer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Manufacturer</FormLabel>
                  <FormControl>
                    <Input placeholder="Manufacturer name (optional)" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="portfolioId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Portfolio</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a portfolio" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {portfolios.map((portfolio) => (
                        <SelectItem key={portfolio.id} value={portfolio.id}>
                          {portfolio.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="greenSealStandardId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Green Seal Standard</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select applicable Green Seal standard" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {greenSealStandards.map((standard) => (
                        <SelectItem key={standard.id} value={standard.id}>
                          {standard.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Adding..." : "Add Product"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}